// useStorefrontState.js
// Phase 6: Storefront division - UI state and simple handlers
// API handlers remain in ProjectDetail.js due to external dependencies

import { useState, useCallback } from 'react';

export const useStorefrontState = () => {
  // ============================================
  // OPERATION LOADING STATE (prevents double-submit)
  // ============================================
  const [operationLoading, setOperationLoading] = useState(false);
  
  // ============================================
  // SELECTION STATES
  // ============================================
  const [selectedStorefrontTypeIds, setSelectedStorefrontTypeIds] = useState([]);
  const [selectedSFGlassTypeIds, setSelectedSFGlassTypeIds] = useState([]);
  const [selectedSFMetalPanelTypeIds, setSelectedSFMetalPanelTypeIds] = useState([]);
  const [selectedSFPanelListIds, setSelectedSFPanelListIds] = useState([]);
  const [selectedStorefrontElevationIds, setSelectedStorefrontElevationIds] = useState([]);
  
  // Expanded states
  const [expandedSFGlassTypeIds, setExpandedSFGlassTypeIds] = useState([]);
  const [expandedSFMetalPanelIds, setExpandedSFMetalPanelIds] = useState([]);
  
  // ============================================
  // ELEVATION STATES
  // ============================================
  const [selectedStorefrontElevation, setSelectedStorefrontElevation] = useState(null);
  
  // ============================================
  // SEARCH, FILTER, SORT STATES
  // ============================================
  const [sfScheduleSearch, setSfScheduleSearch] = useState('');
  const [sfScheduleFilters, setSfScheduleFilters] = useState({ type: '', location: '' });
  const [sfScheduleSortBy, setSfScheduleSortBy] = useState('createdAt_desc');

  // ============================================
  // SF TYPE DIALOG STATES
  // ============================================
  const [showSFTypeDialog, setShowSFTypeDialog] = useState(false);
  const [editingSFType, setEditingSFType] = useState(null);
  const [newSFType, setNewSFType] = useState({
    name: '',
    manufacturer: '',
    model: '',
    material: '',
    finishType: '',
    interiorFinishColor: '',
    exteriorFinishColor: '',
    sectionWidth_in: null,
    sectionHeight_in: null,
    roWidth_in: null,
    roHeight_in: null,
    productWarrantyYears: null,
    hardwareWarrantyYears: null,
    finishWarrantyYears: null,
    installationWarrantyYears: null
  });

  // ============================================
  // SF GLASS TYPE DIALOG STATES
  // ============================================
  const [showSFGlassTypeDialog, setShowSFGlassTypeDialog] = useState(false);
  const [editingSFGlassType, setEditingSFGlassType] = useState(null);
  const [newSFGlassType, setNewSFGlassType] = useState({
    name: '',
    manufacturer: '',
    model: '',
    thickness_in: null,
    specifications: [],
    colorCode: '',
    argonFilled: false,
    lowECoated: false,
    warrantyYears: '',
    build: '',
    hasGrid: false,
    gridSize_in: ''
  });

  // ============================================
  // SF METAL TYPE DIALOG STATES
  // ============================================
  const [showSFMetalTypeDialog, setShowSFMetalTypeDialog] = useState(false);
  const [editingSFMetalType, setEditingSFMetalType] = useState(null);
  const [newSFMetalType, setNewSFMetalType] = useState({
    name: '',
    manufacturer: '',
    model: '',
    thickness_mm: '',
    colorCode: ''
  });

  // ============================================
  // SF PANEL DIALOG STATES
  // ============================================
  const [showSFPanelDialog, setShowSFPanelDialog] = useState(false);
  const [editingPanel, setEditingPanel] = useState(null);
  const [newSFPanel, setNewSFPanel] = useState({
    storefrontTypeId: '',
    label: '',
    location: '',
    width_in: '',
    height_in: '',
    roWidth_in: '',
    roHeight_in: '',
    lites: []
  });

  // ============================================
  // PICTURE/DRAWING STATES
  // ============================================
  const [pendingSFTypePicture, setPendingSFTypePicture] = useState(null);
  const [sfTypePictureUploading, setSFTypePictureUploading] = useState(null);
  const [sfTypePictureUrl, setSFTypePictureUrl] = useState(null);
  const [showSFDrawingDialog, setShowSFDrawingDialog] = useState(false);
  const [selectedSFTypeForView, setSelectedSFTypeForView] = useState(null);
  const [activeSFTypeForMarker, setActiveSFTypeForMarker] = useState(null);

  // ============================================
  // BULK MARKER STATES
  // ============================================
  const [sfBulkMarkerMode, setSfBulkMarkerMode] = useState(false);
  const [sfBulkMarkerSchedule, setSfBulkMarkerSchedule] = useState('');
  const [pendingSfBulkMarkers, setPendingSfBulkMarkers] = useState([]);
  const [savingSfBulkMarkers, setSavingSfBulkMarkers] = useState(false);

  // ============================================
  // SIMPLE HANDLERS (No API calls)
  // ============================================

  // Reset new SF type form
  const resetNewSFType = useCallback(() => {
    setNewSFType({
      name: '',
      manufacturer: '',
      model: '',
      material: '',
      finishType: '',
      interiorFinishColor: '',
      exteriorFinishColor: '',
      sectionWidth_in: null,
      sectionHeight_in: null,
      roWidth_in: null,
      roHeight_in: null,
      productWarrantyYears: null,
      hardwareWarrantyYears: null,
      finishWarrantyYears: null,
      installationWarrantyYears: null
    });
    setPendingSFTypePicture(null);
  }, []);

  // Reset new SF glass type form
  const resetNewSFGlassType = useCallback(() => {
    setNewSFGlassType({
      name: '',
      manufacturer: '',
      model: '',
      thickness_in: null,
      specifications: [],
      colorCode: '',
      argonFilled: false,
      lowECoated: false,
      warrantyYears: null,
      build: '',
      hasGrid: false,
      gridSize_in: null
    });
  }, []);

  // Reset new SF metal type form
  const resetNewSFMetalType = useCallback(() => {
    setNewSFMetalType({
      name: '',
      manufacturer: '',
      model: '',
      thickness_mm: null,
      colorCode: ''
    });
  }, []);

  // Reset new SF panel form
  const resetNewSFPanel = useCallback(() => {
    setNewSFPanel({
      storefrontTypeId: '',
      label: '',
      location: ''
    });
  }, []);

  // Bulk marker handlers
  const handleCancelSfBulkMarkers = useCallback(() => {
    setSfBulkMarkerMode(false);
    setSfBulkMarkerSchedule('');
    setPendingSfBulkMarkers([]);
  }, []);

  const handleRemovePendingSfMarker = useCallback((markerId) => {
    setPendingSfBulkMarkers(prev => prev.filter(m => m.id !== markerId));
  }, []);

  // Clear all selections
  const clearStorefrontSelections = useCallback(() => {
    setSelectedStorefrontTypeIds([]);
    setSelectedSFGlassTypeIds([]);
    setSelectedSFMetalPanelTypeIds([]);
    setSelectedSFPanelListIds([]);
    setSelectedStorefrontElevationIds([]);
  }, []);

  // ============================================
  // RETURN ALL STATE AND HANDLERS
  // ============================================
  return {
    // Operation loading (prevents double-submit)
    operationLoading,
    setOperationLoading,
    
    // Selection states
    selectedStorefrontTypeIds, setSelectedStorefrontTypeIds,
    selectedSFGlassTypeIds, setSelectedSFGlassTypeIds,
    selectedSFMetalPanelTypeIds, setSelectedSFMetalPanelTypeIds,
    selectedSFPanelListIds, setSelectedSFPanelListIds,
    selectedStorefrontElevationIds, setSelectedStorefrontElevationIds,
    expandedSFGlassTypeIds, setExpandedSFGlassTypeIds,
    expandedSFMetalPanelIds, setExpandedSFMetalPanelIds,
    
    // Elevation states
    selectedStorefrontElevation, setSelectedStorefrontElevation,
    
    // Search, filter, sort
    sfScheduleSearch, setSfScheduleSearch,
    sfScheduleFilters, setSfScheduleFilters,
    sfScheduleSortBy, setSfScheduleSortBy,
    
    // SF Type dialog
    showSFTypeDialog, setShowSFTypeDialog,
    editingSFType, setEditingSFType,
    newSFType, setNewSFType,
    
    // SF Glass Type dialog
    showSFGlassTypeDialog, setShowSFGlassTypeDialog,
    editingSFGlassType, setEditingSFGlassType,
    newSFGlassType, setNewSFGlassType,
    
    // SF Metal Type dialog
    showSFMetalTypeDialog, setShowSFMetalTypeDialog,
    editingSFMetalType, setEditingSFMetalType,
    newSFMetalType, setNewSFMetalType,
    
    // SF Panel dialog
    showSFPanelDialog, setShowSFPanelDialog,
    editingPanel, setEditingPanel,
    newSFPanel, setNewSFPanel,
    
    // Picture/Drawing states
    pendingSFTypePicture, setPendingSFTypePicture,
    sfTypePictureUploading, setSFTypePictureUploading,
    sfTypePictureUrl, setSFTypePictureUrl,
    showSFDrawingDialog, setShowSFDrawingDialog,
    selectedSFTypeForView, setSelectedSFTypeForView,
    activeSFTypeForMarker, setActiveSFTypeForMarker,
    
    // Bulk marker states
    sfBulkMarkerMode, setSfBulkMarkerMode,
    sfBulkMarkerSchedule, setSfBulkMarkerSchedule,
    pendingSfBulkMarkers, setPendingSfBulkMarkers,
    savingSfBulkMarkers, setSavingSfBulkMarkers,
    
    // Simple handlers
    resetNewSFType,
    resetNewSFGlassType,
    resetNewSFMetalType,
    resetNewSFPanel,
    handleCancelSfBulkMarkers,
    handleRemovePendingSfMarker,
    clearStorefrontSelections
  };
};

export default useStorefrontState;
